import { useEffect, useState } from 'react'
import { Box, Button, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material'
import { Copy, RefreshCw, X } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { AppShell, Crumb } from '@agribot/polygon-checks-ui'
import { EmptyState, Eyebrow, Mono, StatusPill, Surface } from '@agribot/polygon-checks-ui'
import { SkeletonGridRows } from '@agribot/polygon-checks-ui'
import { Segmented } from '@agribot/polygon-checks-ui'
import { formatDate, num } from '@agribot/polygon-checks-ui'
import { tokens } from '@agribot/polygon-checks-ui'
import { useAdminOrganisations, useRequestLogs } from './hooks'
import type { RequestLog } from './types'

const COLUMNS = [
  { label: 'Time', width: '150px' },
  { label: 'Status', width: '64px' },
  { label: 'Request', width: 'minmax(0,1.4fr)' },
  { label: 'Caller', width: 'minmax(0,0.9fr)' },
  { label: 'Took', width: '72px', align: 'right' as const },
]
const GRID = COLUMNS.map((c) => c.width).join(' ')

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'error', label: 'Errors' },
  { value: '4xx', label: '4xx' },
  { value: '5xx', label: '5xx' },
]

/** The filters that live in the URL, so a link to a failing call can be sent to someone. */
const FILTERS = ['status', 'organisation_id', 'key_id', 'user_id', 'q'] as const

/**
 * What the API was asked, and how it answered.
 *
 * For helping a partner without opening CloudWatch: find their calls by
 * organisation or key, see what came back, and for a 5xx the error they
 * were not shown. The request id is the way into CloudWatch when this is
 * not enough.
 */
export function Logs() {
  const [params, setParams] = useSearchParams()
  const filters = Object.fromEntries(FILTERS.map((f) => [f, params.get(f) ?? undefined]))

  const set = (key: string, value: string | undefined) =>
    setParams((p) => {
      if (value) p.set(key, value)
      else p.delete(key)
      return p
    }, { replace: true })

  // Typed into a box, sent once the typing stops.
  const [search, setSearch] = useState(filters.q ?? '')
  useEffect(() => {
    const t = setTimeout(() => {
      if ((filters.q ?? '') !== search.trim()) set('q', search.trim() || undefined)
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const organisations = useAdminOrganisations().data ?? []
  const { data, isLoading, isFetching, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useRequestLogs(filters)
  const logs = data?.pages.flatMap((p) => p.logs) ?? []
  const retention = data?.pages[0]?.retention_days

  const [open, setOpen] = useState<number | null>(null)

  // A filter picked from a row, shown by name rather than as a bare uuid.
  const named = (id: string | undefined, pick: (l: RequestLog) => [string | null, string | null]) =>
    id ? logs.map(pick).find(([i]) => i === id)?.[1] ?? id.slice(0, 8) : undefined
  const keyName = named(filters.key_id, (l) => [l.key_id, l.key_name])
  const userName = named(filters.user_id, (l) => [l.user_id, l.user_name])

  return (
    <AppShell crumbs={[<Crumb key="a">Admin</Crumb>, <Crumb key="b">Logs</Crumb>]}>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 7 }}>
        <Eyebrow sx={{ mb: 2 }}>Admin</Eyebrow>
        <Typography variant="h1" sx={{ mb: 1 }}>Logs</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5, maxWidth: 680 }}>
          Every call to the API{retention ? ` in the last ${retention} days` : ''}, across
          every organisation. Headers and request bodies are never kept. Open a
          row for the error behind it and the request id to find it in CloudWatch.
        </Typography>

        {/* --- Filters --- */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
          <Segmented
            ariaLabel="Status"
            options={STATUSES}
            value={filters.status ?? ''}
            onChange={(v) => set('status', v || undefined)}
          />
          <Select
            size="small" displayEmpty sx={{ minWidth: 220 }}
            value={filters.organisation_id ?? ''}
            onChange={(e) => set('organisation_id', e.target.value || undefined)}
          >
            <MenuItem value="">All organisations</MenuItem>
            {organisations.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
          </Select>
          <TextField
            size="small" sx={{ minWidth: 280 }}
            placeholder="URL, error or request id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Box sx={{ flex: 1 }} />
          <Button
            variant="text"
            startIcon={<RefreshCw size={14} />}
            disabled={isFetching}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </Box>

        {(keyName || userName) && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            {keyName && <FilterChip label={`Key: ${keyName}`} onClear={() => set('key_id', undefined)} />}
            {userName && <FilterChip label={`User: ${userName}`} onClear={() => set('user_id', undefined)} />}
          </Box>
        )}

        <Surface sx={{ overflow: 'hidden', mt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, columnGap: 3, px: 5 }}>
            {COLUMNS.map((c) => (
              <Box
                key={c.label}
                sx={{
                  display: 'flex', alignItems: 'center', height: 36,
                  justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
                  font: `500 11px ${tokens.font.sans}`, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'text.disabled',
                }}
              >
                {c.label}
              </Box>
            ))}
            {isLoading && (
              <SkeletonGridRows columns={COLUMNS.length} rows={8} height={44} align={[4]} />
            )}
            {logs.map((log) => (
              <LogRow
                key={log.id}
                log={log}
                open={open === log.id}
                onToggle={() => setOpen(open === log.id ? null : log.id)}
                onFilter={set}
              />
            ))}
          </Box>

          {!isLoading && logs.length === 0 && (
            <EmptyState
              title="No requests match"
              body="Nothing was called with these filters in the period kept."
            />
          )}
        </Surface>

        {hasNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button variant="text" disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
              {isFetchingNextPage ? 'Loading…' : 'Older requests'}
            </Button>
          </Box>
        )}
      </Box>
    </AppShell>
  )
}

/** `08 Oct 2026, 14:03:22` — seconds matter when matching a call to a report. */
function stamp(value: string) {
  const d = new Date(value)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${formatDate(d)}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function statusPill(status: number) {
  const tone = status >= 500 ? 'rejected' : status >= 400 ? 'flagged' : 'passed'
  return <StatusPill status={tone} label={String(status)} />
}

/** Who made the call, as far as the request got before it was answered. */
function caller(log: RequestLog) {
  const who = log.user_name ?? (log.user_id ? `user ${log.user_id.slice(0, 8)}` : null)
  const via = log.key_name ? `key ${log.key_name}` : null
  return {
    primary: log.organisation_name ?? (log.key_name && !log.organisation_id ? 'Platform' : null),
    secondary: [who, via].filter(Boolean).join(' · ') || null,
  }
}

function LogRow({
  log, open, onToggle, onFilter,
}: {
  log: RequestLog
  open: boolean
  onToggle: () => void
  onFilter: (key: string, value: string | undefined) => void
}) {
  const cell = {
    minHeight: 44, display: 'flex', alignItems: 'center', minWidth: 0,
    borderTop: `1px solid ${tokens.color.ink200}`, cursor: 'pointer',
  }
  const who = caller(log)

  return (
    <>
      <Box
        onClick={onToggle}
        sx={{
          display: 'contents',
          '& > *': open ? { backgroundColor: tokens.color.ink50 } : {},
          '&:hover > *': { backgroundColor: tokens.color.ink50 },
        }}
      >
        <Box sx={cell}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {stamp(log.occurred_at)}
          </Typography>
        </Box>
        <Box sx={cell}>{statusPill(log.status)}</Box>
        <Box sx={{ ...cell, gap: 2 }}>
          <Mono sx={{ fontSize: 11, color: 'text.disabled', flex: 'none', width: 44 }}>{log.method}</Mono>
          <Box sx={{ minWidth: 0 }}>
            <Mono sx={{ fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.url}>
              {log.url}
            </Mono>
            {log.error && (
              <Typography
                variant="caption" noWrap title={log.error}
                sx={{ display: 'block', color: log.status >= 500 ? tokens.color.danger : tokens.color.warning }}
              >
                {log.error}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={cell}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ color: who.primary ? 'text.primary' : 'text.disabled' }}>
              {who.primary ?? 'Anonymous'}
            </Typography>
            {who.secondary && (
              <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.disabled' }}>
                {who.secondary}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ ...cell, justifyContent: 'flex-end' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {num(log.duration_ms)} ms
          </Typography>
        </Box>
      </Box>
      {open && <LogDetail log={log} onFilter={onFilter} />}
    </>
  )
}

function LogDetail({
  log, onFilter,
}: { log: RequestLog; onFilter: (key: string, value: string | undefined) => void }) {
  const facts: [string, React.ReactNode][] = [
    ['Request id', log.request_id && <Copyable text={log.request_id} />],
    ['Route', log.route && <Mono sx={{ fontSize: 12 }}>{log.route}</Mono>],
    ['Organisation', log.organisation_id && (
      <FilterLink
        label={log.organisation_name ?? log.organisation_id}
        onClick={() => onFilter('organisation_id', log.organisation_id!)}
      />
    )],
    ['User', log.user_id && (
      <FilterLink
        label={log.user_name ? `${log.user_name} (${log.user_id})` : log.user_id}
        onClick={() => onFilter('user_id', log.user_id!)}
      />
    )],
    ['Key', log.key_id && (
      <FilterLink label={log.key_name ?? log.key_id} onClick={() => onFilter('key_id', log.key_id!)} />
    )],
    ['Signed in by', log.auth_method],
    ['IP', log.ip],
    ['User agent', log.user_agent],
  ]

  return (
    <Box
      sx={{
        gridColumn: '1 / -1', backgroundColor: tokens.color.ink50,
        px: 0, pt: 1, pb: 4, display: 'flex', flexDirection: 'column', gap: 3,
      }}
    >
      {log.error && (
        <Typography variant="body2" sx={{ color: log.status >= 500 ? tokens.color.danger : tokens.color.warning }}>
          {log.error}
        </Typography>
      )}
      {log.error_stack && (
        <Box
          component="pre"
          sx={{
            m: 0, p: 3, maxHeight: 280, overflow: 'auto', borderRadius: 1,
            backgroundColor: tokens.color.ink100, font: `400 11px ${tokens.font.mono}`,
            whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}
        >
          {log.error_stack}
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: '140px minmax(0,1fr)', rowGap: 1.5, columnGap: 3 }}>
        {facts.filter(([, v]) => v).map(([k, v]) => (
          <Box key={k} sx={{ display: 'contents' }}>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>{k}</Typography>
            <Box sx={{ minWidth: 0, font: `400 12px ${tokens.font.sans}`, wordBreak: 'break-all' }}>{v}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function FilterLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Tooltip title="Show only these">
      <Typography
        component="button" variant="caption" onClick={onClick}
        sx={{
          border: 0, background: 'none', p: 0, cursor: 'pointer', textAlign: 'left',
          color: tokens.color.brandDeep, textDecoration: 'underline', textUnderlineOffset: 2,
        }}
      >
        {label}
      </Typography>
    </Tooltip>
  )
}

function Copyable({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Mono sx={{ fontSize: 12 }}>{text}</Mono>
      <Tooltip title={copied ? 'Copied' : 'Copy'}>
        <IconButton
          size="small"
          onClick={() => {
            void navigator.clipboard.writeText(text).then(() => setCopied(true))
          }}
        >
          <Copy size={12} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 1, height: 26, pl: 2.5, pr: 1,
        borderRadius: tokens.radius.pill, backgroundColor: tokens.color.ink100,
        font: `500 12px ${tokens.font.sans}`,
      }}
    >
      {label}
      <IconButton size="small" aria-label="Clear" onClick={onClear} sx={{ p: 0.5 }}>
        <X size={12} />
      </IconButton>
    </Box>
  )
}
